/**
 * Password Strength Utility
 * 
 * Calculates password strength based on multiple criteria:
 * - Lowercase letters
 * - Uppercase letters
 * - Numbers
 * - Special characters
 * - Length > 7
 */

export interface PasswordStrength {
  score: number;
  isValid: boolean;
  className: string;
  label: string;
}

/**
 * Calculate password strength score (0-5)
 * Returns strength object with score, validity, and styling
 */
export const calculatePasswordStrength = (password: string): PasswordStrength => {
  if (!password) {
    return {
      score: 0,
      isValid: false,
      className: 'bg-gray-200',
      label: 'empty',
    };
  }

  let strength = 0;

  // Criteria checks (each adds 1 point)
  if (password.match(/[a-z]+/)) strength += 1;      // Lowercase
  if (password.match(/[A-Z]+/)) strength += 1;      // Uppercase
  if (password.match(/[0-9]+/)) strength += 1;      // Numbers
  if (password.match(/[$@#&!]+/)) strength += 1;    // Special chars
  if (password.length > 7) strength += 1;           // Length

  const strengthMap: Record<number, PasswordStrength> = {
    0: { 
      score: 0, 
      isValid: false, 
      className: 'bg-gray-200',
      label: 'empty',
    },
    1: { 
      score: 1, 
      isValid: false, 
      className: 'bg-red-500',
      label: 'weak',
    },
    2: { 
      score: 2, 
      isValid: false, 
      className: 'bg-orange-500',
      label: 'weak',
    },
    3: { 
      score: 3, 
      isValid: false, 
      className: 'bg-yellow-500',
      label: 'medium',
    },
    4: { 
      score: 4, 
      isValid: false, 
      className: 'bg-lime-500',
      label: 'medium',
    },
    5: { 
      score: 5, 
      isValid: true, 
      className: 'bg-green-500',
      label: 'strong',
    },
  };

  return strengthMap[strength] || strengthMap[5];
};

/**
 * Get password requirements for display
 */
export const getPasswordRequirements = () => [
  'At least 8 characters long',
  'Contains lowercase letters (a-z)',
  'Contains uppercase letters (A-Z)',
  'Contains numbers (0-9)',
  'Contains special characters ($@#&!)',
];

/**
 * Validate password meets minimum requirements
 */
export const isPasswordValid = (password: string): boolean => {
  const strength = calculatePasswordStrength(password);
  return strength.isValid;
};
